import { Box, Link } from "@mui/material";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// Keyframes for right-to-left scroll
const scrollRightToLeft = keyframes`
  0% { transform: translateX(0%); }
  100% { transform: translateX(-50%); }
`;

// Styled track so animation is applied in same style context as keyframes (reliable cross-browser)
const ScrollingTrack = styled(Box)`
  display: flex;
  align-items: center;
  gap: 40px;
  width: max-content;
  animation: ${scrollRightToLeft} 40s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const NewsLogos = ({ isHomePage = false }) => {
  const home = useSelector((state) => state.cms.home);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(true);

  const NEWS_LOGOS = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 67);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    return related
      .filter(r => r && (r.status === 1 || r.status === true))
      .map((r, idx) => ({ id: r.id ?? idx, title: r.title || '', image: r.image || '', link: r.link || '#' }));
  }, [home]);

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || NEWS_LOGOS.length === 0) {
      setNeedsScroll(false);
      return;
    }
    const checkOverflow = () => {
      const contentWidth = measure.scrollWidth;
      const containerWidth = container.clientWidth;
      setNeedsScroll(contentWidth > containerWidth);
    };
    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(container);
    return () => ro.disconnect();
  }, [NEWS_LOGOS]);

  const LogoItem = ({ item, index, isMobile = false }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mx: isMobile ? { xs: 2, sm: 3, md: 4 } : 0,
      }}
    >
      <Link 
        href={item.link || "#"} 
        target="_blank" 
        rel="noopener noreferrer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textDecoration: "none",
          cursor: item.link ? "pointer" : "default"
        }}
      >
        {item.image ? (
          <Box
            component="img"
            src={item.image}
            alt={item.title || `News Logo ${index + 1}`}
            sx={{
              width: "auto",
              height: "auto",
              objectFit: "contain",
              // Equal size for all logos - consistent height
              maxHeight: isMobile ? {
                xs: "60px",
                sm: "70px",
                md: "80px",
                lg: "90px",
              } : {
                xs: "50px",
                sm: "60px",
                md: "70px",
                lg: "80px",
              },
              // Ensure consistent width as well
              maxWidth: isMobile ? {
                xs: "120px",
                sm: "140px",
                md: "160px",
                lg: "180px",
              } : {
                xs: "100px",
                sm: "120px",
                md: "140px",
                lg: "160px",
              },
              ...(isMobile && {
                padding: "5px",
                backgroundColor: "transparent",
                borderRadius: "4px"
              })
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px",
              height: isMobile ? {
                xs: "60px",
                sm: "70px",
                md: "80px",
                lg: "90px",
              } : {
                xs: "50px",
                sm: "60px",
                md: "70px",
                lg: "80px",
              },
              width: isMobile ? {
                xs: "120px",
                sm: "140px",
                md: "160px",
                lg: "180px",
              } : {
                xs: "100px",
                sm: "120px",
                md: "140px",
                lg: "160px",
              },
              fontSize: {
                xs: "12px",
                sm: "14px",
                md: "16px",
              },
              fontWeight: "bold",
              textTransform: "uppercase",
              color: "inherit",
              whiteSpace: "nowrap"
            }}
          >
            {item.title}
          </Box>
        )}
      </Link>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "94.44%",
        maxWidth: "1440px",
        margin: "0 auto",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          overflow: "hidden",
          padding: isHomePage ? "40px 0 30px" : "30px 0",
          position: "relative",
          ...(needsScroll && {
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "50px",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              right: 0,
              top: 0,
              width: "50px",
              height: "100%",
              zIndex: 2,
              pointerEvents: "none",
              background: "linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))",
            },
          }),
        }}
      >
        {/* Hidden row used only to measure total content width */}
        <Box
          ref={measureRef}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            visibility: "hidden",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: "40px",
            width: "max-content",
          }}
        >
          {NEWS_LOGOS.map((item, index) => (
            <LogoItem key={`measure-${index}`} item={item} index={index} isMobile={false} />
          ))}
        </Box>

        {needsScroll ? (
          <ScrollingTrack>
            {NEWS_LOGOS.map((item, index) => (
              <LogoItem key={`first-${index}`} item={item} index={index} isMobile={false} />
            ))}
            {NEWS_LOGOS.map((item, index) => (
              <LogoItem key={`second-${index}`} item={item} index={index} isMobile={false} />
            ))}
          </ScrollingTrack>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "40px",
              flexWrap: "wrap",
            }}
          >
            {NEWS_LOGOS.map((item, index) => (
              <LogoItem key={index} item={item} index={index} isMobile={false} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewsLogos;